import { CssBaseline, PaletteMode, useMediaQuery } from "@mui/material";
import {
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
} from "@mui/material/styles";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const fontFamily = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "sans-serif",
].join(", ");

const createAppTheme = (mode: PaletteMode) =>
  responsiveFontSizes(
    createTheme({
      palette: { mode },
      typography: {
        fontFamily,
        h1: { fontSize: "2.5rem", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.02em" },
        h2: { fontSize: "2rem", fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.015em" },
        h3: { fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.01em" },
        h4: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.3 },
        h5: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 },
        h6: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.5 },
        body1: { fontSize: "1rem", lineHeight: 1.7 },
        body2: { fontSize: "0.9375rem", lineHeight: 1.7 },
      },
    })
  );

type ThemeContextType = {
  toggleTheme: () => void;
  theme: ReturnType<typeof createTheme>;
};

const ThemeContext = React.createContext({} as ThemeContextType);

const ThemeContextProvider = ({ children }: PropsWithChildren) => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [theme, setTheme] = useState(
    createAppTheme(prefersDarkMode ? "dark" : "light")
  );

  const toggleTheme = useCallback(() => {
    setTheme((prevTheme) =>
      createAppTheme(prevTheme.palette.mode === "light" ? "dark" : "light")
    );
  }, []);

  useEffect(() => {
    setTheme(createAppTheme(prefersDarkMode ? "dark" : "light"));
  }, [prefersDarkMode]);

  return (
    <ThemeContext.Provider value={{ toggleTheme, theme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useExtendedTheme = () => {
  return useContext(ThemeContext);
};

export default ThemeContextProvider;
